//@ts-nocheck
import type { Template } from '@/content/templates/template.types';

import { UnsuccessfulFetchError } from '../common/errors';

import type {
	AGGQueryResponse,
	ContentPlatformTemplate,
	ContentPlatformTemplateImageAsset,
} from './agg-client-types';
import type { Node as ProseMirrorDocument } from 'prosemirror-model';

const GATEWAY_URL = '/gateway/api/graphql';

export type AGGClientOptions = {
	gatewayUrl?: string;
};

const DEFAULT_OPTIONS: AGGClientOptions = {
	gatewayUrl: GATEWAY_URL,
};

const GET_TEMPLATES_QUERY = `#graphql
    query getTemplates($first: Int = 50, $after: String) {
        templates(search: {queries: {first: $first, after: $after}}) {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                node {
                    templateId
                    name
                    contributor {
                        __typename
                        ... on ContentPlatformOrganization {
                            ...contentPlatformOrganization
                        }
                        ... on ContentPlatformAuthor {
                            name
                            jobTitle
                            shortBiography
                            authorPicture {
                                ...contentPlatformImageAsset
                            }
                            organization {
                                ...contentPlatformOrganization
                            }
                        }
                    }
                    previewBlurb
                    templateIcon {
                        ...contentPlatformImageAsset
                    }
                    templatePreview {
                        ...contentPlatformImageAsset
                    }
                    oneLinerHeadline
                    aboutThisTemplate
                    howToUseThisTemplate {
                        name
                        steps {
                            name
                            description
                            asset {
                                ...contentPlatformImageAsset
                            }
                        }
                    }
                    category {
                        id
                        name
                        urlSlug
                        shortDescriptionOneLiner
                        description
                    }
                    relatedTemplates {
                        templateId
                    }
                    targetAudience {
                        userRoleId
                        roleDescription
                        userRole
                    }
                    targetOrganizationSize {
                        shortDescription
                        name
                    }
                    templateType {
                        id
                        templateTypeName
                    }
                }
            }
        }
    }

    fragment contentPlatformImageAsset on ContentPlatformTemplateImageAsset {
        description
        title
        url
        details
        fileName
        contentType
    }

    fragment contentPlatformOrganization on ContentPlatformOrganization {
        name
        tagline
        shortDescription
        logo {
            ...contentPlatformImageAsset
        }
        industry {
            name
            shortDescription
        }
    }
    `;

function imageFromContentPlatformImage(node: ContentPlatformTemplateImageAsset) {
	// TODO: Assert the shape of this JSON blob?
	const details = node.details as {
		size: number;
		image?: { height: number; width: number };
	};
	return {
		name: node.title,
		size: details.size,
		height: details.image?.height,
		width: details.image?.width,
		url: node.url,
	};
}

function contributorFromContentPlatformContributor(
	node: ContentPlatformTemplate['contributor'],
): Template['contributor'] {
	if (node?.__typename === 'ContentPlatformOrganization') {
		return {
			name: node.name,
			tagline: node.tagline,
			shortDescription: node.shortDescription,
			logo: node.logo?.map(imageFromContentPlatformImage),
			industry: node.industry?.map((industry) => ({
				name: industry.name,
				shortDescription: industry.shortDescription,
			})),
		};
	} else if (node?.__typename === 'ContentPlatformAuthor') {
		return {
			name: node.name,
			jobTitle: node.jobTitle,
			shortBiography: node.shortBiography,
			authorPicture: node.authorPicture ? imageFromContentPlatformImage(node.authorPicture) : null,
			organization: contributorFromContentPlatformContributor(node.organization),
		};
	}
	return null;
}

function templateFromContentPlatformTemplate(node: ContentPlatformTemplate): Template {
	return {
		templateId: node.templateId,
		name: node.name,
		contributor: contributorFromContentPlatformContributor(node.contributor),
		previewBlurb: node.previewBlurb,
		templateIcon: node.templateIcon ? imageFromContentPlatformImage(node.templateIcon) : null,
		templatePreview: node.templatePreview?.map(imageFromContentPlatformImage),
		oneLinerHeadline: node.oneLinerHeadline,
		aboutThisTemplate: node.aboutThisTemplate as ProseMirrorDocument | null,
		howToUseThisTemplate: node.howToUseThisTemplate?.map((guide) => ({
			name: guide.name,
			steps:
				guide.steps?.map((step) => ({
					name: step.name,
					description: step.description as ProseMirrorDocument | null,
					asset: step.asset ? imageFromContentPlatformImage(step.asset) : null,
				})) ?? [],
		})),
		category: node.category?.map((category) => ({
			id: category.id,
			name: category.name,
			urlSlug: category.urlSlug,
			shortDescriptionOneLiner: category.shortDescriptionOneLiner,
			description: category.description as ProseMirrorDocument | null,
		})),
		relatedTemplates: node.relatedTemplates?.map(templateFromContentPlatformTemplate),
		targetAudience: node.targetAudience,
		targetOrganizationSize: node.targetOrganizationSize?.map((size) => ({
			name: size.name,
			shortDescription: size.shortDescription,
		})),
	};
}

/** AtlassianGraphQLGatewayClient */
export class AGGClient {
	public constructor(private readonly opts: AGGClientOptions = DEFAULT_OPTIONS) {}

	public async query<T = AGGQueryResponse>(
		query: string,
		variables?: object,
		customHeaders?: Record<string, string>,
	): Promise<T> {
		const headers = new Headers({ 'Content-Type': 'application/json' });
		if (customHeaders) {
			Object.keys(customHeaders).forEach((key, value) => {
				headers.append(key, customHeaders[key] ?? '');
			});
		}

		const response = await fetch(this.opts?.gatewayUrl ?? GATEWAY_URL, {
			method: 'POST',
			headers: headers,
			credentials: 'include',
			body: JSON.stringify({ query, variables }),
		});
		if (response.ok) return response.json();

		throw new UnsuccessfulFetchError({ response });
	}

	public async getTemplates(): Promise<Template[]> {
		const templates: Template[] = [];
		let pageInfo;

		do {
			const { data } = await this.query(GET_TEMPLATES_QUERY, {
				after: pageInfo?.endCursor,
			});
			pageInfo = data.templates.pageInfo;
			templates.push(
				...data.templates.edges.map(({ node }) => templateFromContentPlatformTemplate(node)),
			);
		} while (pageInfo?.hasNextPage);

		return templates;
	}
}
